import React from 'react';
import { Link, Modal, Notice, Text } from '@heartinternet/hi-ui';
import type { SSLInfoData } from '../types';

export type GuideKind = 'dns' | 'website';

export interface ShowMeHowModalProps {
  kind: GuideKind;
  isOpen: boolean;
  onClose: () => void;
  info: SSLInfoData;
  manageDomainUrl: string;
}

/** DNS / Website "Show me how" guides — mirrors utilities/control-dns.js + control-website.js. */
export const ShowMeHowModal: React.FC<ShowMeHowModalProps> = ({
  kind,
  isOpen,
  onClose,
  info,
  manageDomainUrl,
}) => {
  if (kind === 'dns') {
    const subdomain = (info.cname_record ?? '').split('.')[0];
    const target = info.cname_value ? `${info.cname_value}.` : '';
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Prove domain ownership using your DNS">
        <div className="hi-ui-ssl-info__guide">
          <Text variant="body">
            1: Go to your DNS settings in the control panel where you manage your domain.
          </Text>
          <Text variant="body">
            If your domain is attached to one of our hosting packages go to{' '}
            <Link href={manageDomainUrl} target="_blank">
              Manage Domain Names
            </Link>
            . Select the domain you want to protect with an SSL and click &apos;Manage Now&apos;,
            then click &apos;DNS Management&apos;.
          </Text>
          <Text variant="body">
            2: Create a CNAME record with the following details (Note: The full stop at the end of
            the address is required — do not omit it.):
          </Text>
          <div className="hi-ui-ssl-info__guide-value">
            <Text variant="inline" bold>
              Subdomain:{' '}
            </Text>
            <Text variant="code">{subdomain}</Text>
          </div>
          <div className="hi-ui-ssl-info__guide-value">
            <Text variant="inline" bold>
              Address / Target:{' '}
            </Text>
            <Text variant="code">{target}</Text>
          </div>
          <Notice variant="info">
            <Text variant="inline" bold>
              Important:
            </Text>{' '}
            <Text variant="body-small">
              For domains using Heart Internet nameservers: do not include the full domain name
              (e.g. .your-domain.co.uk) when entering the subdomain — it will be added
              automatically.
            </Text>
            <Text variant="body-small">
              For domains using external nameservers: you may need to enter the full subdomain
              (_dnsauth.your-domain.co.uk) depending on how your DNS provider handles record
              entries.
            </Text>
          </Notice>
          <Text variant="body">3: Save the changes.</Text>
          <Text variant="body">
            4: Click the &quot;Check status&quot; button to complete the process.
          </Text>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Prove domain ownership using your website">
      <div className="hi-ui-ssl-info__guide">
        <Text variant="body">
          1: Save the following content into a text file named &quot;fileauth.txt&quot;
        </Text>
        <Text variant="code">{info.file_content}</Text>
        <Text variant="body">
          2: Upload the &quot;fileauth.txt&quot; file to the following location:
        </Text>
        <Text variant="code">{info.file_location}</Text>
        <Notice variant="info">
          Please keep in mind that the root of your site will likely be a folder in your hosting
          space, something like &quot;public_html&quot; or &quot;htdocs&quot;. Create the above
          directories and file accordingly.
        </Notice>
        <Text variant="body-small">
          To validate both the root domain and www, or any other FQDN, make this change at further
          locations unless you have a redirect in place.
        </Text>
        <Text variant="body">
          3: Verify the file exists at the specified locations by going to the location in your
          browser.
        </Text>
        <Text variant="body">
          4: Click the &quot;Check status&quot; button to complete the process.
        </Text>
      </div>
    </Modal>
  );
};
